import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowRight,
  Boxes,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  FileSignature,
  Clock,
  IndianRupee,
  ListChecks,
  Package,
  ShieldCheck,
  ShoppingBag,
  Star,
  TrendingUp,
  UtensilsCrossed,
  Wallet,
  MapPin,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill } from '@/components/ui/status-pill';
import { StatCard } from '@/components/ui/stat-card';
import { Skeleton } from '@/components/ui/skeleton';
import { use_draft } from '@/features/onboarding/hooks';
import { use_dashboard_stats } from '@/features/vendor-stats/hooks';
import { use_vendor_agreements } from '@/features/agreements/hooks';
import { use_vendor_profile } from '@/features/vendor-profile/hooks';
import { KYC_PRESENTATION } from '@/features/vendor-profile/kyc-status';
import type { VendorDashboardStats } from '@/features/vendor-stats/types';
import { format_inr as inr } from '@/lib/utils';

interface QuickLink {
  to: string;
  label: string;
  hint: string;
  icon: LucideIcon;
}

/** Quick actions relevant to what the vendor's modules can actually do. */
function quick_links(sources: VendorDashboardStats['sources']): QuickLink[] {
  const links: QuickLink[] = [];
  if (sources.bookings) {
    links.push(
      { to: '/vendor/app/services', label: 'Services', hint: 'Edit your offerings', icon: ListChecks },
      { to: '/vendor/app/availability', label: 'Availability', hint: 'Set your hours', icon: CalendarClock },
      { to: '/vendor/app/bookings', label: 'Bookings', hint: 'See what’s booked', icon: CalendarCheck },
    );
  }
  if (sources.orders) {
    links.push(
      { to: '/vendor/app/menu', label: 'Menu', hint: 'Upload your menu', icon: UtensilsCrossed },
      { to: '/vendor/app/orders', label: 'Orders', hint: 'Fulfil food orders', icon: ShoppingBag },
    );
  }
  if (sources.shipments) {
    links.push({ to: '/vendor/app/commerce-shipments', label: 'Orders', hint: 'Ship what’s paid', icon: ShoppingBag });
  }
  links.push({ to: '/vendor/app/reviews', label: 'Reviews', hint: 'Reply to customers', icon: Star });
  return links;
}

export function VendorDashboardPage() {
  const draft_q = use_draft();
  const stats_q = use_dashboard_stats();
  const profile_q = use_vendor_profile();
  const agreements_q = use_vendor_agreements();

  const draft = draft_q.data;
  const submitted = Boolean(draft?.submitted_at);
  const modules = draft?.module_types ?? [];
  // Real review outcome, not just "did they submit" — an approved or rejected
  // vendor otherwise sits on "In review" forever.
  const kyc = profile_q.data?.kyc_status
    ? KYC_PRESENTATION[profile_q.data.kyc_status]
    : undefined;
  const stats = stats_q.data;
  const kyc_verified = profile_q.data?.kyc_status === 'VERIFIED';
  const loading = draft_q.isLoading || stats_q.isLoading;

  const links = stats ? quick_links(stats.sources) : [];
  // Primary CTA points at the vendor's main working surface.
  const primary = links[0];
  const pending_agreements = (agreements_q.data?.agreements ?? []).filter((a) => !a.accepted_at);
  const pending_labels = pending_agreements.map((a) => a.module_label);
  const show_agreement_notice = (agreements_q.data?.pending_count ?? 0) > 0;

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Dashboard"
        description="Your business at a glance."
        action={
          primary ? (
            <Button asChild variant="gradient" size="sm">
              <Link to={primary.to} data-testid="dashboard-primary-cta">
                Manage {primary.label.toLowerCase()} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          ) : undefined
        }
      />

      {/*
        Not discoverable yet. Deliberately above everything else and calmly
        worded: the vendor has done nothing wrong and nothing is broken — we
        simply haven't launched where they are. Their console keeps working.
      */}
      {stats?.serviceability && !stats.serviceability.is_visible_to_customers && (
        <Card className="border-2 border-warning/40 bg-warning/10" data-testid="serviceability-notice">
          <CardContent className="flex items-start gap-3 p-4">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
            <div className="min-w-0">
              <p className="font-heading font-bold" data-testid="serviceability-title">
                {stats.serviceability.title}
              </p>
              <p className="text-sm text-muted-foreground" data-testid="serviceability-body">
                {stats.serviceability.body}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/*
        Client change (9 Oct 2026): a highlighted prompt when slots are not added. Solid fill on
        purpose — slash-opacity colour classes render nothing in this theme.
      */}
      {stats && stats.slot_setup.missing_modules.length > 0 && (
        <div
          className="flex flex-col gap-3 rounded-card border-2 border-warning bg-warning p-4 text-warning-foreground sm:flex-row sm:items-center sm:justify-between"
          role="alert"
          data-testid="slot-setup-notice"
        >
          <div className="flex items-start gap-3">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="font-heading font-bold">You haven’t added your slots yet</p>
              <p className="text-sm">
                Customers can’t book {stats.slot_setup.missing_modules.join(', ')} until you add
                your available times.
              </p>
            </div>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0 bg-card text-foreground">
            <Link to="/vendor/app/availability" data-testid="slot-setup-cta">
              Add slots <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}

      {/*
        Unaccepted agreement update: a reminder, never a block. Solid fill for the same reason as
        the slot-setup prompt below.
      */}
      {show_agreement_notice && (
        <div
          className="flex flex-col gap-3 rounded-card border-2 border-info bg-info p-4 text-info-foreground sm:flex-row sm:items-center sm:justify-between"
          role="status"
          data-testid="agreement-update-notice"
        >
          <div className="flex items-start gap-3">
            <FileSignature className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="font-heading font-bold">
                {pending_labels.length > 0
                  ? `Your ${pending_labels.join(', ')} agreement${pending_labels.length > 1 ? 's were' : ' was'} updated — review and accept`
                  : 'Your agreement was updated — review and accept'}
              </p>
              <p className="text-sm">
                Download the latest version and accept it to stay up to date.
              </p>
            </div>
          </div>
          <Button asChild size="sm" variant="outline" className="shrink-0 bg-card text-foreground">
            <Link to="/vendor/app/agreements" data-testid="agreement-update-cta">
              Review agreements <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}

      {/* Revenue row */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-card" />
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="revenue-summary">
          <StatCard label="Earned today" value={inr(stats.revenue.today_inr)} icon={IndianRupee} />
          <StatCard label="This month" value={inr(stats.revenue.this_month_inr)} icon={TrendingUp} />
          <StatCard label="All-time earnings" value={inr(stats.revenue.all_time_inr)} icon={Wallet} />
          <StatCard
            label="Needs action"
            value={stats.pending_actions}
            icon={Clock}
            hint={stats.pending_actions > 0 ? 'Awaiting your response' : 'You’re all caught up'}
          />
        </div>
      ) : null}

      {/* Activity row — module-aware */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="activity-summary">
          {stats.sources.bookings && (
            <>
              <StatCard label="Bookings today" value={stats.activity.bookings_today} icon={CalendarCheck} />
              <StatCard label="Upcoming" value={stats.activity.bookings_upcoming} icon={CalendarPlus} />
            </>
          )}
          {stats.sources.orders && (
            <>
              <StatCard label="Orders today" value={stats.activity.orders_today} icon={ShoppingBag} />
              <StatCard
                label="New orders"
                value={stats.activity.orders_awaiting_action}
                icon={Clock}
                hint="Awaiting accept"
              />
            </>
          )}
          {stats.sources.shipments && (
            <>
              <StatCard label="Shipments today" value={stats.activity.shipments_today} icon={Package} />
              <StatCard
                label="To fulfil"
                value={stats.activity.shipments_awaiting_action}
                icon={Clock}
                hint="Paid, awaiting dispatch"
              />
            </>
          )}
          <StatCard
            label="Rating"
            value={stats.reviews.average != null ? stats.reviews.average.toFixed(1) : '—'}
            icon={Star}
            hint={stats.reviews.total > 0 ? `${stats.reviews.total} review${stats.reviews.total === 1 ? '' : 's'}` : 'No reviews yet'}
          />
        </div>
      )}

      {/* Working area */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Account status */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Account status</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {draft_q.isLoading ? (
              <Skeleton className="h-7 w-60" />
            ) : submitted && kyc ? (
              <div className="flex flex-col gap-2" data-testid="status-submitted">
                <StatusPill tone={kyc.tone}>
                  <CheckCircle2 className="h-4 w-4" /> {kyc.headline}
                </StatusPill>
                <p className="text-sm text-muted-foreground">
                  {/*
                    KYC and serviceability are different questions, and a verified
                    vendor outside a launched region is verified but NOT
                    discoverable. Saying "live and discoverable" here while the
                    banner above says the opposite is worse than either message
                    alone — the vendor cannot tell which one to believe.
                  */}
                  {kyc_verified && stats?.serviceability && !stats.serviceability.is_visible_to_customers
                    ? 'Your documents are approved. Customers will see you as soon as we launch in your area.'
                    : kyc.detail}
                </p>
              </div>
            ) : submitted ? (
              <div className="flex flex-col gap-2" data-testid="status-submitted">
                <StatusPill tone="success">
                  <CheckCircle2 className="h-4 w-4" /> Submitted for KYC review
                </StatusPill>
                <p className="text-sm text-muted-foreground">
                  All features are open while we verify — you’ll be notified once approved.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-start gap-3" data-testid="status-incomplete">
                <StatusPill tone="warning">
                  <Clock className="h-4 w-4" /> Onboarding not submitted yet
                </StatusPill>
                <Button asChild variant="outline" size="sm" className="w-fit">
                  <Link to="/onboarding">Continue onboarding</Link>
                </Button>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 pt-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> KYC:{' '}
                {kyc?.short ?? (submitted ? 'In review' : 'Pending')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Boxes className="h-4 w-4" /> {modules.length} module{modules.length === 1 ? '' : 's'}
              </span>
            </div>

            {modules.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Your modules
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {modules.map((m) => (
                    <StatusPill key={m} tone="neutral">
                      {m}
                    </StatusPill>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick actions */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-button" />)
              : links.map((q) => (
                  <Link
                    key={`${q.to}-${q.label}`}
                    to={q.to}
                    className="group flex items-center gap-3 rounded-button border-2 border-brand-outline-variant/15 bg-secondary/40 px-3 py-2.5 transition-colors duration-fast hover:border-primary/30 hover:bg-secondary"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-button bg-card text-primary shadow-clay-sm">
                      <q.icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold leading-tight">{q.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">{q.hint}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-fast group-hover:translate-x-0.5" />
                  </Link>
                ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
